"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getChangesAfterCleaningInterview = exports.auditInterview = exports.ANONYMOUS_PARTICIPANT_PREFIX = exports.INTERVIEWER_PARTICIPANT_PREFIX = void 0;
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const get_1 = __importDefault(require("lodash/get"));
const set_1 = __importDefault(require("lodash/set"));
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
/**
 * Prefix used for the username of the participants created for a
 * computer-aided telephone interview (CATI)
 * */
exports.INTERVIEWER_PARTICIPANT_PREFIX = 'telephone';
/**
 * Prefix used for the username of participants created by anonymous login
 * TODO: use a single constant with chaire-lib's anonymousLoginStrategy (`anonym_`)
 */
exports.ANONYMOUS_PARTICIPANT_PREFIX = 'anonym';
/**
 * Runs the audit validations on the interview data and returns the audit result
 *
 * @param interview The Interview to audit
 * @param validations TODO Type
 * @param surveyProjectHelper TODO Type
 * @returns The audit result, where the key is the ID of the validation to run
 * and the value is the number of times this validation fails on the response.
 */
// FIXME: move this to backend
const auditInterview = function (correctedResponse, originalResponse, interview, validations, surveyProjectHelper) {
    const auditsCountByValidationId = {};
    const response = correctedResponse ? correctedResponse : originalResponse;
    const household = (0, get_1.default)(response, 'household', {});
    const home = (0, get_1.default)(response, 'home', {});
    const correctedInterview = (0, cloneDeep_1.default)(interview);
    correctedInterview.response = response;
    // FIXME: Redesign this with cloneDeep at the right place.
    //        Audit should not touch the interview data, but we should make sure of that.
    // TODO Rethink this, the _response field does not exist on the type and should not have to exist
    correctedInterview._response = originalResponse;
    const persons = surveyProjectHelper.getPersons(correctedInterview, false);
    const personsArray = surveyProjectHelper.getPersons(correctedInterview, true);
    const audits = [];
    audits.push(...surveyProjectHelper.auditInterview(validations.interview, null, {}, correctedInterview, response, household, home, persons, personsArray).audits);
    audits.push(...surveyProjectHelper.auditHousehold(validations.household, null, {}, correctedInterview, response, household, home, persons, personsArray).audits);
    for (const personId in persons) {
        const person = persons[personId];
        audits.push(...surveyProjectHelper.validatePerson(validations.person, null, {}, correctedInterview, response, household, home, persons, personsArray, person).audits);
        const visitedPlaces = surveyProjectHelper.getVisitedPlaces(person, null, false);
        const visitedPlacesArray = surveyProjectHelper.getVisitedPlaces(person, null, true);
        for (let i = 0, count = visitedPlacesArray.length; i < count; i++) {
            const visitedPlace = visitedPlacesArray[i];
            audits.push(...surveyProjectHelper.validateVisitedPlace(validations.visitedPlace, null, {}, correctedInterview, response, household, home, persons, personsArray, person, visitedPlaces, visitedPlacesArray, visitedPlace).audits);
        }
        const trips = surveyProjectHelper.getTrips(person, false);
        const tripsArray = surveyProjectHelper.getTrips(person, true);
        for (let i = 0, count = tripsArray.length; i < count; i++) {
            const trip = tripsArray[i];
            audits.push(...surveyProjectHelper.validateTrip(validations.trip, null, {}, correctedInterview, response, household, home, persons, personsArray, person, visitedPlaces, visitedPlacesArray, trips, tripsArray, trip).audits);
            const segments = surveyProjectHelper.getSegments(trip, false);
            const segmentsArray = surveyProjectHelper.getSegments(trip, true);
            for (let j = 0, countJ = segmentsArray.length; j < countJ; j++) {
                const segment = segmentsArray[j];
                audits.push(...surveyProjectHelper.validateSegment(validations.segment, null, {}, correctedInterview, response, household, home, persons, personsArray, person, visitedPlaces, visitedPlacesArray, trips, tripsArray, trip, segments, segmentsArray, segment).audits);
            }
        }
    }
    for (let i = 0, count = audits.length; i < count; i++) {
        const audit = audits[i];
        if (!auditsCountByValidationId[audit.id]) {
            auditsCountByValidationId[audit.id] = 0;
        }
        auditsCountByValidationId[audit.id]++;
    }
    return auditsCountByValidationId;
};
exports.auditInterview = auditInterview;
// this function will use parsers to clean and fix the interviews. Parsers can change any path in the interview, to normalize repsonses or clean errors or typos in some fields:
// the input is corrected_response response if available, otherwise, it will use original respondent response
// output will be valuesByPath which are each changes made to the response, by path
const getChangesAfterCleaningInterview = function (correctedResponse, originalResponse, interview, parsers, surveyProjectHelper) {
    // TODO: type this function and parsers
    const valuesByPath = {};
    let changeModesWalk = false;
    const response = correctedResponse || originalResponse;
    const validatedInterview = (0, cloneDeep_1.default)(interview);
    validatedInterview.response = response;
    validatedInterview._response = originalResponse;
    const household = response.household || {};
    const home = response.home || {};
    const persons = surveyProjectHelper.getPersons(validatedInterview, false);
    const personsArray = surveyProjectHelper.getPersons(validatedInterview, true);
    for (let i = 0, count = parsers.length; i < count; i++) {
        const parser = parsers[i];
        for (const path in parser.parsers.interview) {
            const newValue = parser.parsers.interview[path]({}, validatedInterview, response, household, home, persons, personsArray);
            const absolutePath = path;
            const oldValue = (0, get_1.default)(validatedInterview, absolutePath);
            if (!(0, isEqual_1.default)(newValue, oldValue)) {
                valuesByPath[path] = newValue;
                (0, set_1.default)(validatedInterview, absolutePath, newValue); // update values
            }
        }
        for (const path in parser.parsers.household) {
            if (household) {
                const newValue = parser.parsers.household[path]({}, validatedInterview, response, household, home, persons, personsArray);
                const absolutePath = 'response.household.' + path;
                const oldValue = (0, get_1.default)(validatedInterview, absolutePath);
                if (!(0, isEqual_1.default)(newValue, oldValue)) {
                    valuesByPath[absolutePath] = newValue;
                    (0, set_1.default)(validatedInterview, absolutePath, newValue); // update values
                    (0, set_1.default)(household, path, newValue); // update values
                }
            }
        }
        for (const personId in persons) {
            const person = persons[personId];
            for (const path in parser.parsers.person) {
                const newValue = parser.parsers.person[path]({}, validatedInterview, response, household, home, persons, personsArray, person);
                const absolutePath = `response.household.persons.${personId}.` + path;
                const oldValue = (0, get_1.default)(validatedInterview, absolutePath);
                if (!(0, isEqual_1.default)(newValue, oldValue)) {
                    valuesByPath[absolutePath] = newValue;
                    (0, set_1.default)(validatedInterview, absolutePath, newValue); // update values
                    (0, set_1.default)(person, path, newValue); // update values
                }
            }
            const visitedPlaces = surveyProjectHelper.getVisitedPlaces(person, null, false);
            const visitedPlacesArray = surveyProjectHelper.getVisitedPlaces(person, null, true);
            for (const visitedPlaceId in visitedPlaces) {
                const visitedPlace = visitedPlaces[visitedPlaceId];
                for (const path in parser.parsers.visitedPlace) {
                    const newValue = parser.parsers.visitedPlace[path]({}, validatedInterview, response, household, home, persons, personsArray, person, visitedPlaces, visitedPlacesArray, visitedPlace);
                    const absolutePath = `response.household.persons.${personId}.visitedPlaces.${visitedPlaceId}.` + path;
                    const oldValue = (0, get_1.default)(validatedInterview, absolutePath);
                    if (!(0, isEqual_1.default)(newValue, oldValue)) {
                        valuesByPath[absolutePath] = newValue;
                        (0, set_1.default)(validatedInterview, absolutePath, newValue); // update values
                        (0, set_1.default)(visitedPlace, path, newValue); // update values
                    }
                }
            }
            const trips = surveyProjectHelper.getTrips(person, false);
            const tripsArray = surveyProjectHelper.getTrips(person, true);
            for (const tripId in trips) {
                const trip = trips[tripId];
                for (const path in parser.parsers.trip) {
                    const newValue = parser.parsers.trip[path]({}, validatedInterview, response, household, home, persons, personsArray, person, trips, tripsArray, trip);
                    const absolutePath = `response.household.persons.${personId}.trips.${tripId}.` + path;
                    const oldValue = (0, get_1.default)(validatedInterview, absolutePath);
                    if (!(0, isEqual_1.default)(newValue, oldValue)) {
                        valuesByPath[absolutePath] = newValue;
                        (0, set_1.default)(validatedInterview, absolutePath, newValue); // update values
                        (0, set_1.default)(trip, path, newValue);
                        if (absolutePath.endsWith('segments')) {
                            //console.log(path, newValue, trip.segments);
                            changeModesWalk = true;
                        }
                    }
                }
                const segments = surveyProjectHelper.getSegments(trip, false);
                const segmentsArray = surveyProjectHelper.getSegments(trip, true);
                for (const segmentId in segments) {
                    const segment = segments[segmentId];
                    for (const path in parser.parsers.segment) {
                        const newValue = parser.parsers.segment[path]({}, validatedInterview, response, household, home, persons, personsArray, person, trips, tripsArray, trip, segments, segmentsArray, segment);
                        const absolutePath = `response.household.persons.${personId}.trips.${tripId}.segments.${segmentId}.` + path;
                        const oldValue = (0, get_1.default)(validatedInterview, absolutePath);
                        if (!(0, isEqual_1.default)(newValue, oldValue)) {
                            valuesByPath[absolutePath] = newValue;
                            (0, set_1.default)(validatedInterview, absolutePath, newValue); // update values
                            (0, set_1.default)(segment, path, newValue);
                        }
                    }
                }
            }
        }
    }
    if (changeModesWalk) {
        //console.log('valuesByPath', valuesByPath);
    }
    return valuesByPath;
};
exports.getChangesAfterCleaningInterview = getChangesAfterCleaningInterview;
//# sourceMappingURL=interview.js.map